import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { IntlContext } from "context";
import { Dot, MapPin, MoreVertical, RefreshCw, Timer, Users } from "lucide-react";
import moment from "moment";
import { useDeleteAd, useUpdateAd } from "hooks/ad";
import { AdFrequency, AdInterface } from "types/ad";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { buildRoute } from "routes";
import { formatAdServiceLocation } from "helpers/formatAdServiceLocation";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { getCategoryStyle } from "../../MyServices/categoryStyle";
import { toast } from "sonner";

interface Props {
    ad: AdInterface;
    onUpdated: () => void;
}

const AdCard: React.FC<Props> = ({ ad, onUpdated }) => {
    const intl = useContext(IntlContext);
    const updateAdMutation = useUpdateAd();
    const deleteAdMutation = useDeleteAd();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const categoryKey = ad.category.toString();
    const { Icon, boxClass, iconClass } = getCategoryStyle(categoryKey);
    const subCategory = ad.subcategory as SubCategory | undefined;

    const title =
        ad.title?.trim() ||
        (subCategory
            ? intl.formatMessage(
                  `subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`
              )
            : intl.formatMessage(`category.${categoryKey.toLocaleLowerCase()}`));

    const subcategoryLabel = subCategory ? intl.formatMessage(`subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`) : "";
    const location = formatAdServiceLocation(ad);
    const applicationsCount = ad.providers_apply?.length ?? 0;
    const isRecurring = ad.frequency === AdFrequency.RECURRING;
    const displayDate = ad.data?.start_date ?? ad.created_at;
    const formattedDate = moment(displayDate).locale("fr").format("D MMMM YYYY");

    const handleToggleActive = async () => {
        try {
            await updateAdMutation.mutateAsync({
                id: ad.id,
                data: { ...ad, is_active: !ad.is_active } as unknown as HashMap<unknown>,
            });
            onUpdated();
        } catch {
            // Keep UI unchanged on failure.
        }
    };

    const handleDeleteAd = async () => {
        try {
            await deleteAdMutation.mutateAsync(ad.id);
            setShowDeleteModal(false);
            toast.success(intl.formatMessage("ad.message.delete_success"));
            onUpdated();
        } catch {
            // Keep the dialog open so the user can retry or cancel.
        }
    };

    const statusBadge = ad.is_active ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {intl.formatMessage("ad.card.status_active")}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
            {intl.formatMessage("ad.card.status_closed")}
        </span>
    );

    const candidatesContent = (
        <>
            <div className="text-2xl font-bold leading-none text-gray-900">{applicationsCount}</div>
            <div className="mt-0.5 text-xs text-slate-500">{intl.formatMessage("ad.card.candidates")}</div>
        </>
    );

    return (
        <article className="relative rounded-2xl border border-gray-200 bg-white p-4 transition-colors hover:bg-slate-50 sm:p-5">
            <Link
                to={buildRoute.myAdDetails(ad.id)}
                className="absolute inset-0 z-[1] cursor-pointer rounded-2xl"
                aria-label={intl.formatMessage("ad.card.aria_open_ad", { title })}
            />
            <div className="relative z-[2] flex flex-col gap-4 pointer-events-none xl:flex-row xl:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                    <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${boxClass}`}
                    >
                        <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-bold text-gray-900">{title}</h3>
                            <Badge
                                variant="frequency"
                                palette={isRecurring ? "violet" : "orange"}
                                className="normal-case tracking-normal"
                            >
                                {isRecurring ? <RefreshCw /> : <Timer />}
                                {isRecurring
                                    ? intl.formatMessage("ad.card.frequency_recurring")
                                    : intl.formatMessage("ad.card.frequency_one_time")}
                            </Badge>
                        </div>
                        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-slate-500">
                            <span>{subcategoryLabel}</span>
                            {subcategoryLabel && <Dot className="h-4 w-4" />}
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="h-4 w-4 shrink-0" />
                                <span className="truncate">{location}</span>
                            </span>
                            <Dot className="h-4 w-4" />
                            <span className="capitalize">{formattedDate}</span>
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 sm:gap-6 xl:ml-auto xl:grid xl:w-auto xl:grid-cols-[4.5rem_5.5rem_auto_auto] xl:gap-x-6">
                    <div className="flex w-[4.5rem] flex-col items-center text-center">
                        {applicationsCount > 0 ? (
                            <Link
                                to={buildRoute.myAdApplications(ad.id)}
                                className="relative z-[3] flex flex-col items-center rounded-lg transition-opacity hover:opacity-80 pointer-events-auto"
                            >
                                {candidatesContent}
                            </Link>
                        ) : (
                            candidatesContent
                        )}
                    </div>

                    <div className="flex w-[5.5rem] justify-center">
                        {statusBadge}
                    </div>

                    <div className="relative z-[3] pointer-events-auto">
                        <Button variant="outline" size="sm" className="rounded-xl" asChild>
                            <Link to={buildRoute.ad(ad.id)} target="_blank">
                                {intl.formatMessage("ad.card.view_public_ad")}
                            </Link>
                        </Button>
                    </div>

                    <div className="relative z-[3] pointer-events-auto">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-lg">
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">{intl.formatMessage("ad.card.actions")}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                            <DropdownMenuItem asChild>
                                <Link to={buildRoute.myAdEdit(ad.id)}>{intl.formatMessage("ad.card.edit")}</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void handleToggleActive()}>
                                {ad.is_active
                                    ? intl.formatMessage("ad.label.disable")
                                    : intl.formatMessage("ad.label.enable")}
                            </DropdownMenuItem>
                            {applicationsCount > 0 && (
                                <DropdownMenuItem asChild>
                                    <Link to={buildRoute.myAdApplications(ad.id)}>
                                        <Users className="mr-2 h-4 w-4" />
                                        {intl.formatMessage("ad.card.applications", { count: applicationsCount })}
                                    </Link>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                onSelect={() => setShowDeleteModal(true)}
                            >
                                {intl.formatMessage("ad.label.delete")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                </div>
            </div>

            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{intl.formatMessage("ad.label.modal.title")}</DialogTitle>
                        <DialogDescription>
                            {intl.formatMessage("ad.label.modal.message")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                            {intl.formatMessage("generic.action.cancel")}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteAdMutation.isPending}
                            onClick={() => void handleDeleteAd()}
                        >
                            {intl.formatMessage("generic.action.confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </article>
    );
};

export default AdCard;
